import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import useCurrentEmployee from "../../hooks/useCurrentEmployee";
import ActionButton from "../../components/query/button";
import {
  listConflictCases,
  createConflictCase,
  updateConflictCase,
  addConflictNote,
  listConflictParticipants,
  getConflictCaseById,
} from "../../api/conflicts";
import ExpandableSearch from "../../components/ExpandableSearch";
import ViewActionButton from "../../components/table/ViewActionButton";
import FetchLoadingState from "../../components/feedback/FetchLoadingState";

const statusOptions = [
  "All",
  "Reported",
  "Under Review",
  "Mediation Scheduled",
  "Action Required",
  "Resolved",
  "Closed",
];

const priorityOptions = ["Low", "Medium", "High", "Critical"];
const categoryOptions = [
  "Interpersonal",
  "Performance",
  "Process",
  "Resource",
  "Policy",
  "Other",
];

const StatCard = ({ title, value, caption, className = "" }) => (
  <div className={`min-w-0 w-full rounded-xl border border-[#0000001A] bg-white p-3 shadow-sm sm:p-4 lg:p-5 ${className}`}>
    <p className="truncate text-xs font-medium text-gray-500 sm:text-sm">{title}</p>
    <p className="mt-2 text-xl font-bold leading-none text-[#0F172A] sm:mt-3 sm:text-2xl lg:text-3xl">{value}</p>
    {caption && <p className="mt-1.5 truncate text-[11px] text-[#64748B] sm:mt-2 sm:text-xs lg:text-sm">{caption}</p>}
  </div>
);

const StatusPill = ({ status }) => {
  const classes = {
    Reported: "bg-[#F1F5F9] text-[#0F172A]",
    "Under Review": "bg-[#F8FAFC] text-[#0F172A] border border-[#94A3B8]",
    "Mediation Scheduled": "bg-[#E0F2FE] text-[#0369A1]",
    "Action Required": "bg-[#FEF3C7] text-[#B45309]",
    Resolved: "bg-[#DCFCE7] text-[#166534]",
    Closed: "bg-[#FEE2E2] text-[#991B1B]",
  };
  return (
    <span className={`inline-flex items-center rounded-full px-3 py-1 text-xs font-semibold ${classes[status] || classes.Reported}`}>
      {status}
    </span>
  );
};

const formatDate = (value) => {
  if (!value) return "—";
  return new Date(value).toLocaleDateString(undefined, {
    month: "short",
    day: "numeric",
    year: "numeric",
  });
};

const ConflictResolution = () => {
  const [analytics, setAnalytics] = useState({
    total: 0,
    active: 0,
    reported: 0,
    underReview: 0,
    mediation: 0,
    actionRequired: 0,
    resolved: 0,
    closed: 0,
  });
  const [conflicts, setConflicts] = useState([]);
  const [participants, setParticipants] = useState([]);
  const [filterStatus, setFilterStatus] = useState("All");
  const [selectedConflict, setSelectedConflict] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [isUpdating, setIsUpdating] = useState(false);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [newNote, setNewNote] = useState("");
  const [searchTerm, setSearchTerm] = useState("");
  const navigate = useNavigate();

  const [createPayload, setCreatePayload] = useState({
    title: "",
    description: "",
    category: "Interpersonal",
    triggerSource: "Manual Report",
    priority: "Medium",
    reporterId: "",
    respondentId: "",
    mediatorId: "",
    desiredOutcome: "",
    dueDate: "",
  });

  const [updatePayload, setUpdatePayload] = useState({
    status: "",
    priority: "Medium",
    mediatorId: "",
    resolutionSummary: "",
    dueDate: "",
  });

  const { companyAccount, currentEmployee, loading: employeeLoading } = useCurrentEmployee();
  const positionName = `${currentEmployee?.positionName || currentEmployee?.position?.name || ""}`.toLowerCase();
  const isAdmin = companyAccount || currentEmployee?.modulePermissions?.includes("conflict-resolution");
  const [workspace, setWorkspace] = useState("admin");
  const isHod = !isAdmin && (positionName.includes("head") || positionName.includes("hod") || !!currentEmployee?.isHeadOfDepartment);
  const isManager = isAdmin || isHod;
  const userRoleLabel = isAdmin ? "Administrator" : isHod ? "Department Head" : "Employee";

  const isEmployeeCase = (conflict) => {
    const currentId = currentEmployee?.id;
    return currentId
      ? [conflict.reporter?.id, conflict.respondent?.id, conflict.mediator?.id].includes(currentId)
      : false;
  };

  const isDepartmentCase = (conflict) => {
    const departmentId = currentEmployee?.department?.id;
    const departmentName = currentEmployee?.department?.name;
    if (!departmentId && !departmentName) return false;

    return [
      conflict.reporter?.departmentId,
      conflict.respondent?.departmentId,
      conflict.mediator?.departmentId,
    ].includes(departmentId)
      || [
        conflict.reporter?.departmentName,
        conflict.respondent?.departmentName,
        conflict.mediator?.departmentName,
      ].includes(departmentName);
  };

  const visibleConflicts = useMemo(() => {
    if (isAdmin && (companyAccount || workspace === "admin")) return conflicts;
    if (isAdmin) return conflicts.filter(isEmployeeCase);
    if (isHod) return conflicts.filter(isDepartmentCase);
    return conflicts.filter(isEmployeeCase);
  }, [conflicts, currentEmployee, isAdmin, isHod, workspace, companyAccount]);

  const searchedConflicts = useMemo(() => {
    const query = searchTerm.trim().toLowerCase();
    if (!query) return visibleConflicts;
    return visibleConflicts.filter((conflict) =>
      [conflict.title, conflict.category, conflict.priority, conflict.status, conflict.reporter?.name, conflict.respondent?.name]
        .some((value) => String(value || "").toLowerCase().includes(query)),
    );
  }, [searchTerm, visibleConflicts]);

  const displayedAnalytics = useMemo(() => {
    if (isAdmin && (companyAccount || workspace === "admin")) {
      return analytics;
    }

    return {
      total: visibleConflicts.length,
      active: visibleConflicts.filter((item) => !["Resolved", "Closed"].includes(item.status)).length,
      reported: visibleConflicts.filter((item) => item.status === "Reported").length,
      underReview: visibleConflicts.filter((item) => item.status === "Under Review").length,
      mediation: visibleConflicts.filter((item) => item.status === "Mediation Scheduled").length,
      actionRequired: visibleConflicts.filter((item) => item.status === "Action Required").length,
      resolved: visibleConflicts.filter((item) => item.status === "Resolved").length,
      closed: visibleConflicts.filter((item) => item.status === "Closed").length,
    };
  }, [analytics, isAdmin, visibleConflicts, workspace, companyAccount]);

  const isRoleLoading = !companyAccount && employeeLoading;
  const isFetchingData = isLoading || isRoleLoading;

  const refreshConflictList = async (status = filterStatus) => {
    setIsLoading(true);
    try {
      const response = await listConflictCases({ status });
      setConflicts(Array.isArray(response?.conflicts) ? response.conflicts : []);
      setAnalytics(response?.analytics || analytics);
    } catch (error) {
      toast.error(error?.response?.data?.message || "Unable to load conflict cases.");
    } finally {
      setIsLoading(false);
    }
  };

  const loadParticipants = async () => {
    try {
      const response = await listConflictParticipants();
      setParticipants(Array.isArray(response?.participants) ? response.participants : []);
    } catch (error) {
      toast.error(error?.response?.data?.message || "Unable to load conflict participants.");
    }
  };

  useEffect(() => {
    refreshConflictList("All");
    loadParticipants();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleCreateFieldChange = (key, value) => {
    setCreatePayload((prev) => ({ ...prev, [key]: value }));
  };

  const submitNewConflict = async () => {
    if (!createPayload.title.trim() || !createPayload.description.trim()) {
      toast.error("Please enter a title and description.");
      return;
    }
    setIsSaving(true);
    try {
      const payload = {
        ...createPayload,
        title: createPayload.title.trim(),
        description: createPayload.description.trim(),
        reporterId: createPayload.reporterId || currentEmployee?.id || undefined,
      };

      await createConflictCase(payload);
      toast.success("Conflict reported successfully.");
      setShowCreateModal(false);
      setCreatePayload({
        title: "",
        description: "",
        category: "Interpersonal",
        triggerSource: "Manual Report",
        priority: "Medium",
        reporterId: "",
        respondentId: "",
        mediatorId: "",
        desiredOutcome: "",
        dueDate: "",
      });
      refreshConflictList("All");
    } catch (error) {
      toast.error(error?.response?.data?.message || "Unable to create conflict.");
    } finally {
      setIsSaving(false);
    }
  };

  const handleSelectConflict = (conflict) => {
    if (!conflict?.id) return;
    navigate(`/conflict-resolution/${encodeURIComponent(conflict.id)}`);
  };

  const submitConflictUpdate = async () => {
    if (!selectedConflict?.id) {
      toast.error("Select a conflict case first.");
      return;
    }
    setIsUpdating(true);
    try {
      await updateConflictCase(selectedConflict.id, {
        status: updatePayload.status,
        priority: updatePayload.priority,
        mediatorId: updatePayload.mediatorId || null,
        resolutionSummary: updatePayload.resolutionSummary,
        dueDate: updatePayload.dueDate || null,
      });
      toast.success("Conflict updated successfully.");
      refreshConflictList(filterStatus);
    } catch (error) {
      toast.error(error?.response?.data?.message || "Unable to update conflict.");
    } finally {
      setIsUpdating(false);
    }
  };

  const submitNewNote = async () => {
    if (!selectedConflict?.id) {
      toast.error("Select a conflict case first.");
      return;
    }
    if (!newNote.trim()) {
      toast.error("Please enter a note before saving.");
      return;
    }
    setIsUpdating(true);
    try {
      await addConflictNote(selectedConflict.id, { body: newNote.trim() });
      toast.success("Note added to the conflict case.");
      setNewNote("");
      refreshConflictList(filterStatus);
    } catch (error) {
      toast.error(error?.response?.data?.message || "Unable to add note.");
    } finally {
      setIsUpdating(false);
    }
  };

  const selectedRowClass = (index) =>
    `border-b border-[#E2E8F0] ${index % 2 === 0 ? "bg-[#FFFFFF]" : "bg-[#F8FAFC]"}`;

  const participantOptions = useMemo(
    () =>
      participants.map((employee) => ({
        id: employee.id,
        label: `${employee.firstName} ${employee.lastName}${employee.departmentName ? ` | ${employee.departmentName}` : ""}`.trim(),
      })),
    [participants]
  );

  const roleHintText = isAdmin
    ? "Full access to all conflict cases and role assignments."
    : isHod
      ? "You can review and manage conflict cases involving your department."
      : "You can report or follow conflicts where you are involved. Your HOD and admin will manage escalation.";

  return (
    <div className="min-h-screen p-4 sm:p-6">
      <div className="border-b border-[#E2E8F0] pb-4 text-center sm:pb-6">
        {isAdmin && !companyAccount ? <div className="mb-4 flex justify-center"><div className="inline-flex rounded-xl border bg-white p-1 shadow-sm" role="group" aria-label="Conflict workspace"><button type="button" onClick={() => setWorkspace("admin")} className={`rounded-lg px-4 py-2 text-xs font-bold ${workspace === "admin" ? "bg-[#1234CD] text-white" : "text-slate-500"}`}>Admin view</button><button type="button" onClick={() => setWorkspace("employee")} className={`rounded-lg px-4 py-2 text-xs font-bold ${workspace === "employee" ? "bg-[#1234CD] text-white" : "text-slate-500"}`}>My conflicts</button></div></div> : null}
        <h1 className="text-xl font-bold text-black sm:py-2 sm:text-2xl">Conflict Resolution</h1>
        <p className="mx-auto mt-1 max-w-3xl text-xs leading-5 text-[#475569] sm:mt-0 sm:text-sm sm:leading-normal">
          Capture, manage and resolve workplace conflict cases with transparent status tracking, role-based review and notification support.
        </p>
        <p className="mt-2 text-[11px] uppercase tracking-[0.16em] text-[#2563EB] sm:mt-3 sm:text-xs sm:tracking-[0.2em]">
          Role: {userRoleLabel}
        </p>
        <p className="mx-auto mt-1 max-w-3xl text-xs leading-5 text-[#475569] sm:mt-2 sm:text-sm sm:leading-normal">
          {roleHintText}
        </p>
        <div className="mt-4 flex gap-2 sm:mt-6 sm:items-center sm:justify-center sm:gap-3">
          <ActionButton className="flex-1 px-2 text-xs sm:flex-none sm:text-base" label="New Conflict Report" onClick={() => setShowCreateModal(true)} />
          <ActionButton className="flex-1 px-2 text-xs sm:flex-none sm:text-base" label="Refresh cases" variant="outline" onClick={() => refreshConflictList(filterStatus)} />
        </div>
      </div>

      <div className="mt-4 space-y-4 sm:mt-8 sm:space-y-6">
        <div className="grid grid-cols-2 gap-2.5 sm:gap-3 md:grid-cols-4 lg:gap-4 xl:grid-cols-7">
          <StatCard title="Active conflicts" value={displayedAnalytics.active} caption={`${displayedAnalytics.total} total`} />
          <StatCard title="Resolved" value={displayedAnalytics.resolved} caption={`${displayedAnalytics.closed} closed`} />
          <StatCard title="Action required" value={displayedAnalytics.actionRequired} caption="Pending review or mediation" />
          <StatCard title="Reported" value={displayedAnalytics.reported} />
          <StatCard title="Under review" value={displayedAnalytics.underReview} />
          <StatCard title="In mediation" value={displayedAnalytics.mediation} />
          <StatCard title="Closed" value={displayedAnalytics.closed} className="col-span-2 md:col-span-1" />
        </div>

        <div className="space-y-6">
          <div className="rounded-2xl border border-[#E2E8F0] bg-white p-4 shadow-sm sm:rounded-3xl sm:p-6">
            <div className="flex flex-col gap-4 md:flex-row md:items-center md:justify-between">
              <div>
                <p className="text-base font-semibold text-[#0F172A]">Conflict cases</p>
                <p className="text-sm text-[#475569]">Browse cases by current status and respond to escalation needs.</p>
              </div>
              <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:gap-3">
                <label className="text-sm text-[#334155]">Status</label>
                <select
                  value={filterStatus}
                  onChange={(event) => {
                    setFilterStatus(event.target.value);
                    refreshConflictList(event.target.value);
                  }}
                  className="w-full rounded-xl border border-[#CBD5E1] bg-white px-4 py-2.5 text-sm text-[#0F172A] shadow-sm sm:w-auto sm:min-w-[180px] sm:py-2"
                >
                  {statusOptions.map((status) => (
                    <option key={status} value={status}>{status}</option>
                  ))}
                </select>
              </div>
            </div>

            <ExpandableSearch
              value={searchTerm}
              onChange={setSearchTerm}
              placeholder="Search conflict cases"
              label="Search conflict cases"
              className="mt-4"
            />

            <div className="mt-4 w-full max-w-full overflow-x-auto overscroll-x-contain sm:mt-6">
              <table className="min-w-[900px] border-separate border-spacing-y-2 text-left text-sm text-[#334155] md:min-w-full">
                <thead>
                  <tr>
                    <th className="py-3 px-4">Title</th>
                    <th className="py-3 px-4">Category</th>
                    <th className="py-3 px-4">Priority</th>
                    <th className="py-3 px-4">Status</th>
                    <th className="py-3 px-4">Reporter</th>
                    <th className="py-3 px-4">Respondent</th>
                    <th className="py-3 px-4">Due date</th>
                    <th className="py-3 px-4">Action</th>
                  </tr>
                </thead>
                <tbody>
                  {isFetchingData ? (
                    <tr>
                      <td colSpan="8" className="py-6 px-4 text-center text-sm text-[#475569]">
                        <FetchLoadingState message="Loading conflict cases..." className="min-h-[260px]" />
                      </td>
                    </tr>
                  ) : searchedConflicts.length === 0 ? (
                    <tr>
                      <td colSpan="8" className="py-6 px-4 text-center text-sm text-[#475569]">
                        {searchTerm ? "No matching conflict cases found." : isAdmin
                          ? "No conflict cases found for this status."
                          : isHod
                            ? "No department conflict cases found for this status. Change status filters or ask an admin for broader access."
                            : "No conflict cases found where you are involved. Submit a report or contact your manager if you need support."
                        }
                      </td>
                    </tr>
                  ) : (
                    searchedConflicts.map((conflict, index) => (
                      <tr key={conflict.id} className={selectedRowClass(index)}>
                        <td className="py-4 px-4 font-medium text-[#0F172A]">{conflict.title}</td>
                        <td className="py-4 px-4">{conflict.category}</td>
                        <td className="py-4 px-4">{conflict.priority}</td>
                        <td className="py-4 px-4">
                          <StatusPill status={conflict.status} />
                        </td>
                        <td className="py-4 px-4">{conflict.reporter?.name || "Unknown"}</td>
                        <td className="py-4 px-4">{conflict.respondent?.name || "Unassigned"}</td>
                        <td className="py-4 px-4">{formatDate(conflict.dueDate)}</td>
                        <td className="py-4 px-4">
                          <ViewActionButton
                            onClick={() => handleSelectConflict(conflict)}
                            label={`View conflict case ${conflict.title || "details"}`}
                          />
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </div>

      {showCreateModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 px-4 py-6">
          <div className="w-full max-w-3xl max-h-screen overflow-y-auto rounded-3xl bg-white p-6 shadow-2xl">
            <div className="flex items-start justify-between gap-4">
              <div>
                <p className="text-xl font-bold text-[#0F172A]">New conflict report</p>
                <p className="mt-2 text-sm text-[#475569]">Create a structured conflict case with mediators, priorities, and escalation notes.</p>
              </div>
              <button
                type="button"
                onClick={() => setShowCreateModal(false)}
                className="text-sm font-semibold text-[#475569]"
              >
                Close
              </button>
            </div>

            <div className="mt-6 grid gap-4 lg:grid-cols-2">
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-semibold text-[#0F172A]">Title</label>
                  <input
                    value={createPayload.title}
                    onChange={(event) => handleCreateFieldChange("title", event.target.value)}
                    className="mt-2 w-full rounded-2xl border border-[#CBD5E1] bg-[#F8FAFC] px-4 py-3 text-sm text-[#0F172A]"
                    placeholder="Conflict title"
                  />
                </div>

                <div>
                  <label className="block text-sm font-semibold text-[#0F172A]">Category</label>
                  <select
                    value={createPayload.category}
                    onChange={(event) => handleCreateFieldChange("category", event.target.value)}
                    className="mt-2 w-full rounded-2xl border border-[#CBD5E1] bg-white px-4 py-3 text-sm text-[#0F172A]"
                  >
                    {categoryOptions.map((category) => (
                      <option key={category} value={category}>{category}</option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-semibold text-[#0F172A]">Priority</label>
                  <select
                    value={createPayload.priority}
                    onChange={(event) => handleCreateFieldChange("priority", event.target.value)}
                    className="mt-2 w-full rounded-2xl border border-[#CBD5E1] bg-white px-4 py-3 text-sm text-[#0F172A]"
                  >
                    {priorityOptions.map((priority) => (
                      <option key={priority} value={priority}>{priority}</option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-semibold text-[#0F172A]">Reporter</label>
                  <select
                    value={createPayload.reporterId}
                    onChange={(event) => handleCreateFieldChange("reporterId", event.target.value)}
                    className="mt-2 w-full rounded-2xl border border-[#CBD5E1] bg-white px-4 py-3 text-sm text-[#0F172A]"
                  >
                    <option value="">Self or auto-assigned</option>
                    {participantOptions.map((participant) => (
                      <option key={participant.id} value={participant.id}>{participant.label}</option>
                    ))}
                  </select>
                  <p className="mt-2 text-xs text-[#64748B]">
                    Reporter will default to your profile unless you choose another employee.
                  </p>
                </div>

                <div>
                  <label className="block text-sm font-semibold text-[#0F172A]">Respondent</label>
                  <select
                    value={createPayload.respondentId}
                    onChange={(event) => handleCreateFieldChange("respondentId", event.target.value)}
                    className="mt-2 w-full rounded-2xl border border-[#CBD5E1] bg-white px-4 py-3 text-sm text-[#0F172A]"
                  >
                    <option value="">Select employee</option>
                    {participantOptions.map((participant) => (
                      <option key={participant.id} value={participant.id}>{participant.label}</option>
                    ))}
                  </select>
                </div>
              </div>

              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-semibold text-[#0F172A]">Mediator</label>
                  <select
                    value={createPayload.mediatorId}
                    onChange={(event) => handleCreateFieldChange("mediatorId", event.target.value)}
                    className="mt-2 w-full rounded-2xl border border-[#CBD5E1] bg-white px-4 py-3 text-sm text-[#0F172A]"
                  >
                    <option value="">Recommended mediator</option>
                    {participantOptions.map((participant) => (
                      <option key={participant.id} value={participant.id}>{participant.label}</option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-semibold text-[#0F172A]">Due date</label>
                  <input
                    type="date"
                    value={createPayload.dueDate}
                    onChange={(event) => handleCreateFieldChange("dueDate", event.target.value)}
                    className="mt-2 w-full rounded-2xl border border-[#CBD5E1] bg-white px-4 py-3 text-sm text-[#0F172A]"
                  />
                </div>

                <div>
                  <label className="block text-sm font-semibold text-[#0F172A]">Trigger source</label>
                  <input
                    value={createPayload.triggerSource}
                    onChange={(event) => handleCreateFieldChange("triggerSource", event.target.value)}
                    className="mt-2 w-full rounded-2xl border border-[#CBD5E1] bg-[#F8FAFC] px-4 py-3 text-sm text-[#0F172A]"
                    placeholder="Manual Report"
                  />
                </div>

                <div>
                  <label className="block text-sm font-semibold text-[#0F172A]">Desired outcome</label>
                  <textarea
                    value={createPayload.desiredOutcome}
                    onChange={(event) => handleCreateFieldChange("desiredOutcome", event.target.value)}
                    rows={4}
                    className="mt-2 w-full resize-none rounded-2xl border border-[#CBD5E1] bg-[#F8FAFC] px-4 py-3 text-sm text-[#0F172A]"
                    placeholder="What do you want to achieve from resolution?"
                  />
                </div>
              </div>
            </div>

            <div className="mt-6">
              <label className="block text-sm font-semibold text-[#0F172A]">Description</label>
              <textarea
                value={createPayload.description}
                onChange={(event) => handleCreateFieldChange("description", event.target.value)}
                rows={4}
                className="mt-2 w-full resize-none rounded-3xl border border-[#CBD5E1] bg-[#F8FAFC] px-4 py-4 text-sm text-[#0F172A]"
                placeholder="Describe the conflict and what needs to be resolved."
              />
            </div>

            <div className="mt-6 flex flex-col gap-3 sm:flex-row sm:justify-end">
              <button
                type="button"
                onClick={() => setShowCreateModal(false)}
                className="rounded-xl border border-[#CBD5E1] bg-white px-5 py-3 text-sm font-semibold text-[#475569]"
              >
                Cancel
              </button>
              <ActionButton
                label={isSaving ? "Saving report..." : "Create conflict"}
                onClick={submitNewConflict}
              />
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ConflictResolution;
